import { useEffect, useState } from "react";
import {
  Book,
  Calendar,
  CalendarPlus,
  Home,
  List,
  LogOut,
  Upload,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { supabase } from "../../../../lib/supabase";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  exactPath?: string;
  subItems?: NavItem[];
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);

  const navigationItems: NavItem[] = [
    {
      label: "Dashboard",
      path: "/coordinator",
      icon: <Home className="size-5" />,
      exactPath: "/coordinator",
    },
    {
      label: "Meetings",
      path: "/coordinator/meetings",
      icon: <Calendar className="size-5" />,
      exactPath: "/coordinator/meetings",
      subItems: [
        {
          label: "New Meeting",
          path: "/coordinator/meetings/new",
          icon: <CalendarPlus className="size-5" />,
        },
      ],
    },
    {
      label: "Mentors",
      path: "/coordinator/mentors",
      icon: <Users className="size-5" />,
      exactPath: "/coordinator/mentors",
      subItems: [
        {
          label: "Add Mentor",
          path: "/coordinator/mentors/add",
          icon: <UserPlus className="size-5" />,
        },
      ],
    },
    {
      label: "Mentees",
      path: "/coordinator/mentees",
      icon: <Users className="size-5" />,
      exactPath: "/coordinator/mentees",
      subItems: [
        {
          label: "Add Mentee",
          path: "/coordinator/mentees/add",
          icon: <UserPlus className="size-5" />,
        },
      ],
    },
    {
      label: "New Assignment",
      path: "/coordinator/assignments/new",
      icon: <UserPlus className="size-5" />,
      exactPath: "/coordinator/assignments/new",
    },
    {
      label: "Assignments",
      path: "/coordinator/assignments",
      icon: <List className="size-5" />,
      exactPath: "/coordinator/assignments",
    },
    {
      label: "Knowledge",
      path: "/coordinator/knowledge-manager",
      icon: <Book className="size-5" />,
      exactPath: "/coordinator/knowledge-manager",
      subItems: [
        {
          label: "Upload Document",
          path: "/coordinator/knowledge-manager/upload",
          icon: <Upload className="size-5" />,
        },
      ],
    },
  ];

  useEffect(() => {
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    };
    getUser();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUserEmail(null);
    setShowMenu(false);
    navigate("/auth/login");
  };

  const isActivePath = (item: NavItem) => {
    if (location.pathname === item.exactPath) {
      return true;
    }
    if (item.subItems) {
      return item.subItems.some((subItem) =>
        location.pathname.includes(subItem.path)
      );
    }
    return false;
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".user-menu")) {
        setShowMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <aside className="fixed left-0 top-0 flex h-screen w-64 flex-col border-r bg-white">
      {/* Navigation Items */}
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {navigationItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
              }}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
                isActivePath(item)
                  ? "bg-[#f0f7f0] text-[#4CAF50]"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* User Profile and Help Section at Bottom */}
      <div className="space-y-2 border-t p-4">
        {userEmail && (
          <div className="user-menu relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="flex w-full items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50"
            >
              <div className="flex size-10 items-center justify-center rounded-full bg-[#4CAF50]">
                <User className="size-5 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h2 className="font-semibold text-gray-900">
                  {userEmail.split("@")[0]}
                </h2>
                <p className="truncate text-sm text-gray-500">{userEmail}</p>
              </div>
            </button>

            {/* Flyout Menu */}
            {showMenu && (
              <div className="absolute bottom-full left-0 mb-2 w-full rounded-lg border bg-white py-1 shadow-lg">
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                >
                  <LogOut className="size-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
